import { Container, Graphics } from 'pixi.js';

/**
 * STAKE-phase clock-fill ring — sweeps an arc around the wheel
 * orbit indicating how much of the STAKE window remains. Producer
 * brief 2026-05-12: the player needs a peripheral cue that the
 * deploy window is ticking down, in addition to the hub
 * countdown digit.
 *
 * <p>Implemented as a pair of arcs:
 * <ol>
 *   <li>Faint backing ring (always visible)</li>
 *   <li>Bright "elapsed" arc that grows from 0° to 360° as time
 *       elapses</li>
 * </ol>
 */

export interface WheelResolveStakeRingOptions {
  /** Diameter of the imaginary ring the arc sits on. */
  diameter: number;
  /** Stroke thickness in pixels. */
  strokeWidth?: number;
}

export class WheelResolveStakeRing extends Container {
  private readonly backing: Graphics;
  private readonly arc: Graphics;
  private radius: number;
  private readonly strokeWidth: number;

  constructor(options: WheelResolveStakeRingOptions) {
    super();
    this.radius = options.diameter / 2;
    this.strokeWidth = options.strokeWidth ?? 4;
    this.backing = new Graphics();
    this.addChild(this.backing);
    this.arc = new Graphics();
    this.addChild(this.arc);
    this.refreshBacking();
  }

  /** Producer brief 2026-05-16 — resize the ring radius rather
   *  than scaling the container, so the stroke width stays at
   *  its native pixel size. */
  setDiameter(diameter: number): void {
    if (diameter / 2 === this.radius) return;
    this.radius = diameter / 2;
    this.refreshBacking();
  }

  private refreshBacking(): void {
    this.backing.clear();
    this.backing
      .circle(0, 0, this.radius)
      .stroke({ color: 0xffffff, alpha: 0.18, width: this.strokeWidth });
  }

  /** Render the elapsed arc covering {@code progress} of a full
   *  circle (0..1). Pass {@link visible} false to hide both layers. */
  setProgress(progress: number): void {
    const clamped = Math.max(0, Math.min(1, progress));
    this.arc.clear();
    if (clamped <= 0.001) return;
    const startRad = -Math.PI / 2; // 12 o'clock
    const endRad = startRad + clamped * Math.PI * 2;
    this.arc.arc(0, 0, this.radius, startRad, endRad);
    this.arc.stroke({ color: 0xffd86b, alpha: 0.9, width: this.strokeWidth });
  }

  destroyDisplay(): void {
    this.removeChildren();
  }
}
